import { useState, useEffect } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableHead, TableRow, Paper,
    Alert, CircularProgress, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Api from '../Services/Api';

function TemplateDoc() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        Api.get({ endpoint: 'pdf-template/doc', contentType: 'application/json' })
            .then(res => setData(res.data))
            .catch(() => setError('Errore nel caricamento della documentazione.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;

    const { system_placeholders, templates } = data;

    // Sintassi letterale dei segnaposto Word mostrata a scopo documentativo.
    // eslint-disable-next-line no-template-curly-in-string
    const placeholderSyntax = '${chiave}';

    const typeLabel = (type) => {
        switch (type) {
            case 'date':     return 'Data';
            case 'currency': return 'Importo';
            case 'integer':  return 'Numero intero';
            default:         return 'Testo';
        }
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Documentazione Template DOCX</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Inserisci i segnaposto nel documento Word usando la sintassi <code>{placeholderSyntax}</code>.
                I segnaposto di sistema sono disponibili in ogni template e vengono compilati automaticamente.
                Quelli personalizzati dipendono dal template selezionato e vengono richiesti all'utente al momento della generazione.
            </Typography>

            <Typography variant="h6" gutterBottom>Segnaposto di sistema</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Presenti in ogni template. Se l'impianto non è selezionato, vengono sostituiti con <em>N/D</em>.
            </Typography>
            <Paper variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Segnaposto</strong></TableCell>
                            <TableCell><strong>Descrizione</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {system_placeholders.map(({ key, label }) => (
                            <TableRow key={key}>
                                <TableCell><code>{'${' + key + '}'}</code></TableCell>
                                <TableCell>{label}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Typography variant="h6" gutterBottom>Segnaposto per template</Typography>
            {templates.length === 0 && (
                <Alert severity="info">Nessun template attivo trovato.</Alert>
            )}
            {templates.map(template => {
                const entries = template.placeholders ? Object.entries(template.placeholders) : [];
                return (
                    <Accordion key={template.id} variant="outlined" sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight="medium">{template.title}</Typography>
                            {entries.length === 0 && (
                                <Chip label="solo segnaposto di sistema" size="small" sx={{ ml: 2 }} />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            {entries.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Questo template usa solo i segnaposto di sistema.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Segnaposto</strong></TableCell>
                                            <TableCell><strong>Label nel form</strong></TableCell>
                                            <TableCell><strong>Tipo</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entries.map(([label, val]) => {
                                            const key  = typeof val === 'object' ? val.key  : val;
                                            const type = typeof val === 'object' ? (val.type ?? 'text') : 'text';
                                            return (
                                                <TableRow key={key}>
                                                    <TableCell><code>{'${' + key + '}'}</code></TableCell>
                                                    <TableCell>{label}</TableCell>
                                                    <TableCell>{typeLabel(type)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}

export default TemplateDoc;
